import { CurrentUserCfg } from 'OK/CurrentUserCfg';
import * as logger from 'OK/logger';
import type {
    Track,
    MusicLayerConfiguration,
    TrackChangedEvent,
    CurrentPlayItem,
    AddTrackPayload,
    PlaylistId,
    TrackChangedWebSubscriber,
    TrackChangedObjectSubscriber,
    ICurrentTrack
} from 'OK/music2/types';
import type { MusicLayer } from 'OK/music2/types';
import * as musicBootstrap from 'music/bootstrap';

const ACTIVATION_DELAY = 2500;

export const MUSIC_LAYER_ID = 'music_layer';

// устанавливаем конфигурацию для леера музыки window.OK
setConfiguration();

// не RequireJSHook
export function activate() {
    // Важно здесь вернуть модель, т.к. activate сейчас используется в сообщениях
    subscribeToMusicApp();
    return musicBootstrap.initModel();
}

export function mountLayer(target: HTMLElement, url?: string): Promise<MusicLayer> {
    subscribeToMusicApp();
    preloadPts();

    return musicBootstrap.mountLayer(target, {
        id: MUSIC_LAYER_ID,
        url
    });
}

export function mountMiniPlayer(target: HTMLElement): Promise<HTMLElement> {
    subscribeToMusicApp();
    preloadPts();

    return musicBootstrap.mountMiniPLayer(target);
}

export function mountMusicPicker(target: HTMLElement): Promise<HTMLElement> {
    subscribeToMusicApp();
    preloadPts();
    return musicBootstrap.mountMusicPicker(target);
}

export function unmountMusicPicker(target: HTMLElement): Promise<void> {
    subscribeToMusicApp();
    return musicBootstrap.unmountMusicPicker(target);
}

let isPlaying = false;
let currentPlayItem: CurrentPlayItem | null = null;
let currentQueue: CurrentPlayItem | null = null;

export function playing(): boolean {
    return isPlaying;
}

export function getCurrentPlayItem(): CurrentPlayItem {
    return currentPlayItem!;
}

export function getCurrentQueue(): CurrentPlayItem {
    return currentQueue!;
}

export function getCurrentTrack(): ICurrentTrack | undefined {
    if (currentPlayItem?.track) {
        return {
            ...currentPlayItem?.track,
            playing: isPlaying,
        }
    }
}

export function togglePlaying() {
    subscribeToMusicApp();
    return musicBootstrap.togglePlaying();
}

export function seekForward() {
    subscribeToMusicApp();
    return musicBootstrap.seekForward();
}

export function seekBackward() {
    subscribeToMusicApp();
    return musicBootstrap.seekBackward();
}

export function louder() {
    subscribeToMusicApp();
    return musicBootstrap.louder();
}

export function quieter() {
    subscribeToMusicApp();
    return musicBootstrap.quieter();
}

export function nextTrack() {
    subscribeToMusicApp();
    return musicBootstrap.nextTrack();
}

export function prevTrack() {
    subscribeToMusicApp();
    return musicBootstrap.prevTrack();
}

export function pause() {
    subscribeToMusicApp();
    return musicBootstrap.pause();
}

export function play(trackId: number, options: object) {
    subscribeToMusicApp();
    return musicBootstrap.play(trackId, options);
}

export function addPlaylist(playlistId: number) {
    subscribeToMusicApp();
    return musicBootstrap.addPlaylist(playlistId);
}

export function removePlaylist(playlistId: number) {
    subscribeToMusicApp();
    return musicBootstrap.removePlaylist(playlistId);
}

export function addTrack(trackId: number, ctx: string): Promise<AddTrackPayload> {
    subscribeToMusicApp();
    return musicBootstrap.addTrack(trackId, ctx);
}

export function removeTrack(trackId: number): Promise<void> {
    subscribeToMusicApp();
    return musicBootstrap.removeTrack(trackId);
}

export function playQueue(playlistType: number, playlistId?: PlaylistId, ctx?: string) {
    subscribeToMusicApp();
    return musicBootstrap.playQueue(playlistType, playlistId, ctx);
}

export function loadMyTracks(): Promise<Track[]> {
    subscribeToMusicApp();
    return musicBootstrap.loadMyTracks();
}

export function searchTracks(query: string): Promise<Track[]> {
    subscribeToMusicApp();
    return musicBootstrap.searchTracks(query);
}

export function share(entityType: string, trackId: number, playlistId: number) {
    subscribeToMusicApp();
    return musicBootstrap.share(entityType, trackId, playlistId);
}

const subscribers = new Set<TrackChangedWebSubscriber>();

// _ для обратной совместимости
export function on(_: string, subscriber: TrackChangedWebSubscriber): void {
    subscribers.add(subscriber);
}

export function off(_: string, subscriber: TrackChangedWebSubscriber): void {
    subscribers.delete(subscriber)
}

export function once(_: string, subscriber: TrackChangedWebSubscriber): void {
    const wrapper = (event: TrackChangedEvent) => {
        off(_, wrapper);
        evaluate(subscriber, event);
    };

    on(_, wrapper);
}

function notify(event: TrackChangedEvent): void {
    subscribers.forEach(subscriber => evaluate(subscriber, event));
}

function evaluate(subscriber: TrackChangedWebSubscriber, event: TrackChangedEvent): void {
    try {
        if (isObjectSubscriber(subscriber)) {
            subscriber.handleTrackChange(event);
        } else {
            subscriber(event);
        }
    } catch (error) {
        logger.clob('music.app.error', (error as Error).stack, 'notify', event);
    }
}

function isObjectSubscriber(subscriber: TrackChangedWebSubscriber): subscriber is TrackChangedObjectSubscriber {
    return subscriber.hasOwnProperty('handleTrackChange')
        || Object.getPrototypeOf(subscriber).hasOwnProperty('handleTrackChange');
}

let subscribed = false;
let initialCallOfChangeListener = true;

function subscribeToMusicApp() {
    if (subscribed) {
        return;
    }

    subscribed = true;

    // порядок подписки важен, так как эти данные могут использоваться в слушателях,
    // зарегистрированных через on()
    musicBootstrap.onPlayItemChanged(payload => {
        isPlaying = payload.playing;

        currentPlayItem = {
            ...payload
        }
    });

    musicBootstrap.onTrackChanged(event => {
        // в первый раз обработчик вызывается сразу при подписке,
        // в данном случае это не нужно
        if (initialCallOfChangeListener) {
            initialCallOfChangeListener = false;
            return;
        }

        notify(event);
    });
}

function setConfiguration(): void {
    if (!OK?.musicConfiguration) {
        // стор музыки создается один раз при первом импорте не в функции
        // пока нет другого способа передать конфигурацию для него
        OK.musicConfiguration = buildConfiguration();
    }
}

function buildConfiguration(): MusicLayerConfiguration {
    const configHolder = document.getElementById('music_layer_holder');

    const uid = CurrentUserCfg.getInstance().getUserId();

    if (configHolder) {
        return {
            uid,
            conf: JSON.parse(configHolder.getAttribute('data-conf')!),
            // будет распаршен в ok-music-web
            confJson: configHolder.getAttribute('data-conf-json')!,
            tooltips: JSON.parse(configHolder.getAttribute('data-tooltips')!),
        }
    }

    return {
        uid,
        conf: {},
        confJson: ''
    }
}

function preloadPts(): void {
    import('OK/pts!music.app');
}

export const v2 = true;

// дублируем экспорты для совместимости сборки
export default {
    activate,
    mountLayer,
    mountMiniPlayer,
    mountMusicPicker,
    unmountMusicPicker,
    playing,
    getCurrentPlayItem,
    getCurrentQueue,
    getCurrentTrack,
    togglePlaying,
    seekForward,
    seekBackward,
    louder,
    quieter,
    nextTrack,
    prevTrack,
    pause,
    play,
    addPlaylist,
    removePlaylist,
    addTrack,
    removeTrack,
    playQueue,
    loadMyTracks,
    searchTracks,
    share,
    on,
    off,
    once,
    v2,
}

setTimeout(activate, ACTIVATION_DELAY);
